import { observer } from 'mobx-react-lite'
import React, { useCallback, useRef } from 'react'
import {
  ActivityIndicator,
  Alert,
  Image,
  Linking,
  ScrollView,
  StatusBar,
  StyleSheet,
  TouchableOpacity
} from 'react-native'
import Animated, { FadeIn } from 'react-native-reanimated'

import { addDaysToDate, formatDate } from '@ppwr/poker'
import { useFocusEffect, useRouter } from 'expo-router'

import ArrowLeft from '../../assets/arrow-left.svg'
import CrownGradient from '../../assets/crown-gradient.png'
import DividerGradient from '../../assets/divider-gradient.svg'
import RadialBackground from '../../assets/minor-radial-background.svg'
import { DS, DSIcon } from '../../design-system'
import { useStore } from '../../models'
import { capitalizeFirstLetter } from '../../utils/formatString'
import FAQSection from './components/FAQSection'
import { useSubscriptionGuard } from '../../hooks/useSubscriptionGuard'

export const ButtonLink = ({ icon, onPress }: { icon: string; onPress: () => void }) => {
  return (
    <DS.Frame style={styles.buttonLinkContainer}>
      <TouchableOpacity style={styles.buttonLink} onPress={onPress}>
        <DS.Frame style={styles.buttonLinkIconContainer}>
          <DS.Frame style={styles.buttonLinkIcon}>
            <DS.Icon name={icon as DSIcon} size={24} color="Global/White" />
          </DS.Frame>
          <DS.Text variant="Body Medium/Regular">{'Commynity'}</DS.Text>
        </DS.Frame>
        <DS.Icon name="Arrow--right-wide" size={16} color="Global/Black" />
      </TouchableOpacity>
    </DS.Frame>
  )
}

const MembershipScreen: React.FC = observer(() => {
  const router = useRouter()
  const {
    game: { setToast },
    analytics: { setUserIdentify },
    session: { user },
    environment: { stage },
    memberships: { membershipTypes, subscription, getMySubscription, getMembershipType, keepUserSubscription, isApple }
  } = useStore()
  const scrollViewRef = useRef<ScrollView>(null)
  const matchingType = membershipTypes.find((item) => item.type === subscription?.type)
  const descriptionArray = matchingType?.description?.split(',') ?? []
  const [loading, setLoading] = React.useState(false)

  const leftPress = () => {
    router.navigate('/(root)/(tabs)')
  }

  useSubscriptionGuard()
  const goToStripeAccount = () => {
    router.navigate('/lobby/membership-stripe')
  }

  const handleKeepMySubscription = async () => {
    if (!subscription?.stripeSubscriptionId || typeof subscription?.stripeSubscriptionId !== 'string') {
      Alert.alert('Invalid subscription ID')
      return
    }

    if (isApple) {
      return Linking.openURL('https://apps.apple.com/account/subscriptions')
    }
    setLoading(true)
    try {
      await keepUserSubscription(subscription?.stripeSubscriptionId)
      user?._id &&
        setUserIdentify(user._id, {
          membership_type: `${subscription.type}_user`
        })
      setTimeout(async () => {
        await getMySubscription()
      }, 1000)
      setToast({
        kind: 'success',
        message: 'Membership updated'
      })
    } catch (e) {
      console.error('handleKeepMySubscription API Error: ', e)
    } finally {
      setLoading(false)
    }
  }

  const renderFeatures = (feature: string) => {
    const ButtonLink = ({ icon, onPress }: { icon: string; onPress: () => void }) => {
      return (
        <DS.Frame style={styles.buttonLinkContainer}>
          <TouchableOpacity style={styles.buttonLink} onPress={onPress}>
            <DS.Frame style={styles.buttonLinkIconContainer}>
              <DS.Frame style={styles.buttonLinkIcon}>
                <DS.Icon name={icon as DSIcon} size={24} color="Global/White" />
              </DS.Frame>
              <DS.Text variant="Body Medium/Regular">{feature}</DS.Text>
            </DS.Frame>
            <DS.Icon name="Arrow--right-wide" size={16} color="Global/Black" />
          </TouchableOpacity>
        </DS.Frame>
      )
    }
    switch (feature.trim()) {
      case 'Community Access':
        //NOTE: Hide community entry point - https://peak6.atlassian.net/browse/PP-3709
        // return (
        //   communityAccessEnabled && (
        //     <ButtonLink
        //       icon="Globe"
        //       onPress={() => {
        //         navigation.navigate(DrawerRoutes.community, {
        //           url: stage === 'prod' ? COMMUNITY_URL : COMMUNITY_URL_DEV
        //         })
        //       }}
        //     />
        //   )
        // )
        return null
      case 'Unlimited On-Demand Classes.':
      case '40+ On-Demand Video Lessons':
        return <ButtonLink icon="Book" onPress={() => router.navigate('/(root)/(tabs)/learning')} />
      case '1 Teaching Table per month.':
      case '2 Teaching Tables per month.':
        return
      case '4 Live Classes per month.':
        return <ButtonLink icon="Camera-Fill-None" onPress={() => router.navigate('/live-event-name')} />
      case 'Student Club Sponsorship.':
        return <ButtonLink icon="Shield" onPress={() => router.navigate('/store')} />
      default:
        return (
          <ButtonLink
            icon="Globe"
            onPress={() => {
              router.navigate('/(root)/(tabs)')
            }}
          />
        )
    }
  }

  useFocusEffect(() => {
    scrollViewRef.current?.scrollTo({ y: 0, animated: true })
  })

  useFocusEffect(
    useCallback(() => {
      try {
        getMySubscription()
        getMembershipType()
      } catch (e) {
        Alert.alert('Error', 'There was an error getting your membership')
      }
    }, [getMySubscription, getMembershipType])
  )

  return (
    <Animated.View style={styles.pageContainer} entering={FadeIn}>
      <StatusBar barStyle={'light-content'} />
      <ScrollView ref={scrollViewRef} showsVerticalScrollIndicator={false} style={styles.container}>
        <DS.Frame style={styles.header}>
          <DS.Frame style={styles.radialBackgroundContainer}>
            <RadialBackground />
          </DS.Frame>
          <DS.Frame style={styles.headerButtonsContainer}>
            <DS.Frame style={styles.backButtonContainer}>
              <TouchableOpacity onPress={leftPress}>
                <ArrowLeft />
              </TouchableOpacity>
            </DS.Frame>
            <Image style={styles.imageSize} source={CrownGradient} resizeMode="contain" />

            {/* <TouchableOpacity onPress={() => homeNavigation.navigate(HomeRoutes.manageMembership)}>
              <DS.Icon name="Switches-white" size={25} color="Global/White" />
            </TouchableOpacity> */}
          </DS.Frame>
        </DS.Frame>

        <DS.Frame style={styles.sectionContainer}>
          <DS.Frame style={styles.sectionTitleContainer}>
            <DS.Text variant="Heading 2/Medium" color="Global/Black">
              {capitalizeFirstLetter(subscription?.type ?? '')} Membership
            </DS.Text>
            <DividerGradient width={40} />
          </DS.Frame>
          <DS.Text variant="Body Medium/Regular" color="Brand/Dark Grey">
            {`Premium since ${formatDate(subscription?.startOfPeriod ?? '')}`}
          </DS.Text>
          {subscription?.status === 'past_due' && (
            <>
              <DS.Frame style={styles.lackPaymentContainer}>
                <DS.Frame style={styles.warningIconContainer}>
                  <DS.Icon name="Info" color="Global/Black" size={20} />
                </DS.Frame>
                <DS.Frame style={styles.lackPaymentTextContainer}>
                  <DS.Text variant="Body Small/Regular">
                    {`We couldn't process your payment. Your membership will be canceled on ${formatDate(
                      addDaysToDate(subscription?.firstPaymentFailedDate ?? '', 5)
                    )}. Please update your billing information to keep your premium benefits.`}
                  </DS.Text>
                </DS.Frame>
              </DS.Frame>

              <TouchableOpacity style={styles.stripeButton} onPress={goToStripeAccount}>
                <DS.Text variant="Body Large/Regular">Stripe Account</DS.Text>
                <DS.Icon name="Arrow--top-right" size={16} color="Global/Black" />
              </TouchableOpacity>

              <DS.Text variant="Body Medium/Medium" color="Brand/Dark Grey">
                You will lose these exclusive features soon.
              </DS.Text>
            </>
          )}
          {(subscription?.cancelAtPeriodEnd || subscription?.pendingDowngrade) && (
            <>
              <DS.Frame style={styles.warningContainer}>
                <DS.Frame style={styles.warningIconContainer}>
                  <DS.Icon name="Info" color="Global/Black" size={20} />
                </DS.Frame>
                <DS.Frame style={styles.warningTextContainer}>
                  <DS.Text variant="Body Small/Regular">{`Membership downgraded to ${
                    subscription?.pendingDowngrade ? 'Silver' : 'Free'
                  }`}</DS.Text>
                  <DS.Text variant="Body Small/Regular" color="Brand/Dark Grey">
                    {subscription?.pendingDowngrade
                      ? formatDate(subscription?.endOfPeriod ?? '')
                      : formatDate(subscription?.cancelAt ?? '')}
                  </DS.Text>
                </DS.Frame>
              </DS.Frame>

              <DS.Text variant="Body Medium/Medium" color="Brand/Dark Grey">
                You will lose these exclusive features soon.
              </DS.Text>
            </>
          )}

          <DS.Frame style={styles.buttonsFeaturesContainer}>
            {descriptionArray.map((feature) => renderFeatures(feature))}
          </DS.Frame>

          {(subscription?.cancelAtPeriodEnd || subscription?.pendingDowngrade) && (
            <DS.Frame style={styles.keepMySubscriptionButton}>
              <DS.Button type="On Light" variant="Large/Primary" onPress={handleKeepMySubscription}>
                {`Keep My ${subscription?.pendingDowngrade ? 'Gold ' : ''}Membership`}
              </DS.Button>

              <DS.Text variant="Caption/Regular" style={styles.keepMembershipText}>
                If you tap Keep My Membership, we won’t cancel it and you’ll continue to enjoy your premium benefits.
              </DS.Text>
              {loading && (
                <DS.Frame style={styles.loadingContainer}>
                  <ActivityIndicator size="small" color="white" />
                </DS.Frame>
              )}
            </DS.Frame>
          )}

          <FAQSection />
        </DS.Frame>
      </ScrollView>
    </Animated.View>
  )
})

const styles = StyleSheet.create({
  pageContainer: {
    backgroundColor: DS.palette['Brand/Cream'],
    flex: 1
  },
  container: {
    backgroundColor: DS.palette['Brand/Cream'],
    paddingBottom: 24,
    position: 'relative'
  },
  headerButtonsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20
  },
  radialBackgroundContainer: {
    transform: [{ translateY: -20 }],
    height: 50,
    backgroundColor: DS.palette['Global/Black'],
    alignItems: 'center'
  },
  header: {
    padding: 20,
    backgroundColor: DS.palette['Global/Black']
  },
  imageSize: {
    width: 30,
    height: 34
  },
  title: {
    fontSize: 24,
    color: DS.palette['Global/White'],
    fontWeight: 'bold'
  },
  cardContainer: {
    marginTop: 32
  },
  contactButton: {
    margin: 20,
    padding: 15,
    borderRadius: 5,
    alignItems: 'center'
  },
  contactButtonText: {
    color: DS.palette['Global/White'],
    fontWeight: 'bold'
  },
  buttonsFeaturesContainer: {
    marginTop: 12,
    marginBottom: 24
  },
  buttonLinkContainer: {
    marginTop: 4
  },
  stripeButton: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: DS.palette['Global/White'],
    padding: 16,
    width: '100%',
    borderRadius: 16,
    marginVertical: 12
  },
  buttonLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: DS.palette['Global/White'],
    paddingVertical: 8,
    paddingLeft: 8,
    paddingRight: 16,
    borderRadius: 48
  },
  buttonLinkIconContainer: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center'
  },
  buttonLinkIcon: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: DS.palette['Global/Black'],
    width: 48,
    height: 48,
    boxShadow: ' 0px 4px 8px 0px #0000001A',
    borderRadius: 48
  },
  backButtonContainer: {},
  sectionContainer: {
    backgroundColor: DS.palette['Brand/Cream'],
    borderTopStartRadius: 24,
    borderTopEndRadius: 24,
    paddingHorizontal: 24,
    transform: [{ translateY: -24 }]
  },
  sectionTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 24
  },
  warningContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 12,
    padding: 8,
    height: 68,
    marginVertical: 12,
    shadowColor: DS.palette['Global/Black'],
    shadowOffset: {
      width: 0,
      height: 4
    },
    shadowOpacity: 0.05,
    elevation: 4
  },
  warningIconContainer: {
    backgroundColor: DS.palette['Brand/Yellow'],
    width: 32,
    height: '100%',
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center'
  },

  warningTextContainer: {
    flex: 1,
    paddingLeft: 8,
    flexDirection: 'column',
    gap: 4,
    justifyContent: 'flex-start'
  },
  lackPaymentContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 12,
    padding: 8,
    height: 105,
    marginTop: 12,
    shadowColor: DS.palette['Global/Black'],
    shadowOffset: {
      width: 0,
      height: 4
    },
    shadowOpacity: 0.05,
    elevation: 4
  },
  lackPaymentTextContainer: {
    flex: 1,
    paddingTop: 8,
    paddingLeft: 8,
    flexDirection: 'column',
    gap: 4,
    justifyContent: 'flex-start'
  },
  keepMySubscriptionButton: {
    position: 'relative'
  },
  loadingContainer: {
    position: 'absolute',
    top: 0,
    left: 220,
    right: 0,
    bottom: 40,
    alignItems: 'center',
    justifyContent: 'center'
  },
  keepMembershipText: {
    paddingTop: 12
  }
})

export default MembershipScreen
